// Import THREE.js FIRST - many modules depend on it being globally available
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { FlyControls } from "three/examples/jsm/controls/FlyControls.js";
import { DDSLoader } from "three/examples/jsm/loaders/DDSLoader.js";
import { platform } from "./controls/platform";

// Make THREE available globally IMMEDIATELY for modules that need it
(window as any).THREE = THREE;
(window as any).OrbitControls = OrbitControls;
(window as any).FlyControls = FlyControls;
(window as any).DDSLoader = DDSLoader;

// Add loaders/controls to THREE namespace for legacy code
(THREE as any).DDSLoader = DDSLoader;
(THREE as any).OrbitControls = OrbitControls;
(THREE as any).FlyControls = FlyControls;

// Now import ES6 modules that may depend on THREE
import { StateManager } from "./states/statemanager";
// Import all states for their side effects (they register themselves)
import "./states/emptystate";
import "./states/gamestate";
import "./states/gameteststate";
import "./states/loginstate";
import "./states/movgenstate";
import "./states/particleteststate";
import "./states/pregamestate";
import "./states/teststate";
import { LoadScreen } from "./helpers/loadscreen";
import { OrthoSprite } from "./helpers/orthosprite";
import { DebugHelper } from "./helpers/debughelper";
import { QuestScriptManager } from "./events/questscriptmanager";
import { ItemDataManager } from "./datamanagers/itemdatamanager";
import { SkillDataManager } from "./datamanagers/skilldatamanager";
import { GZM } from "./objects/zonemanager";
import { UnderwaterOverlay } from "./maps/underwateroverlay";
import { WaterMiddleware } from "./middleware/water-middleware";
import { CameraAnimator } from "./animators/cameraanimator";
import { LoginClient } from "./net/loginclient";
import { GameOptions } from "./objects/gameoptions";
import { DebugLogger } from "./util/logging";
import { GC } from "./helpers/gamecontrol";
import { GCM } from "./helpers/chatmanager";
import { GDM } from "./helpers/datamanager";
import { Font } from "./util/font";
import {
    NETLOGINREPLY,
    PARTY_REQ_MAKE,
    PARTY_REQ_JOIN,
    PARTY_REPLY_ACCEPT_MAKE,
    PARTY_REPLY_NOT_FOUND,
    PARTY_REPLY_BUSY,
    PARTY_REPLY_REJECT_JOIN,
    PARTY_REPLY_DESTROY,
    PARTY_REPLY_FULL_MEMBERS,
    PARTY_REPLY_INVALID_LEVEL,
    PARTY_REPLY_CHANGE_OWNER,
    PARTY_REPLY_CHANGE_OWNERnDISCONN,
    PARTY_REPLY_NO_CHARGE_TARGET,
    PARTY_REPLY_DISCONNECT,
    PARTY_REPLY_BAN,
    PARTY_REPLY_SHARE_ENABLED,
    PARTY_REPLY_SHARE_DISABLED,
    PARTY_RULE_ITEM_TO_ORDER,
    PARTY_RULE_EXP_PER_PLAYER,
    PARTY_MEMBER_SUB,
    REPLY_GET_FIELDITEM_REPLY_OK,
    REPLY_GET_FIELDITEM_REPLY_NONE,
    REPLY_GET_FIELDITEM_REPLY_NO_RIGHT,
    REPLY_GET_FIELDITEM_REPLY_TOO_MANY,
} from "./net/networkconstants";
import { Game } from "./middleware/game";
import { RoseOnlineMiddleware } from "./middleware/rose-online-middleware";
import {
    ITEMLOC,
    INVEQUIPIDX,
    VISEQUIPIDX,
    ITEMTYPE,
    ITEM_DATA,
    WEAPON_DATA,
    ITMPARTTOVISPART,
    ITMPARTTOTYPE,
    ITMTYPETOPART,
    ITMSTACKABLE,
} from "./gamedata/inventorydata";
import { PartyData } from "./gamedata/partydata";

// Create GAME_OPTIONS instance
const GAME_OPTIONS = new GameOptions();

// Middleware pipeline — populated with RoseOnlineMiddleware in the load event
// after the renderer/scene/lights are ready.
const game = new Game();

// Create netConsole for network logging
const netConsole = new DebugLogger(
    "net",
    !(window as any).config?.disableNetLog
);

// Initialize network client globals
(window as any).netLogin = null;
(window as any).netWorld = null;
(window as any).netGame = null;
(window as any).netConsole = netConsole;
(window as any).MC = null;

// Make modules available globally for backward compatibility
(window as any).LoadScreen = LoadScreen;
(window as any).OrthoSprite = OrthoSprite;
(window as any).DebugHelper = DebugHelper;
(window as any).QuestScriptManager = QuestScriptManager;
(window as any).ItemDataManager = ItemDataManager;
(window as any).SkillDataManager = SkillDataManager;
(window as any).GZM = GZM;
(window as any).CameraAnimator = CameraAnimator;
(window as any).LoginClient = LoginClient;
(window as any).GAME_OPTIONS = GAME_OPTIONS;
// Game data constants (for backward compatibility)
(window as any).ITEMLOC = ITEMLOC;
(window as any).INVEQUIPIDX = INVEQUIPIDX;
(window as any).VISEQUIPIDX = VISEQUIPIDX;
(window as any).ITEMTYPE = ITEMTYPE;
(window as any).ITEM_DATA = ITEM_DATA;
(window as any).WEAPON_DATA = WEAPON_DATA;
(window as any).ITMPARTTOVISPART = ITMPARTTOVISPART;
(window as any).ITMPARTTOTYPE = ITMPARTTOTYPE;
(window as any).ITMTYPETOPART = ITMTYPETOPART;
(window as any).ITMSTACKABLE = ITMSTACKABLE;
// Network constants
(window as any).NETLOGINREPLY = NETLOGINREPLY;
(window as any).GC = GC;
(window as any).GCM = GCM;
(window as any).GDM = GDM;
(window as any).Font = Font;
(window as any).PARTY_REQ_MAKE = PARTY_REQ_MAKE;
(window as any).PARTY_REQ_JOIN = PARTY_REQ_JOIN;
(window as any).PARTY_REPLY_ACCEPT_MAKE = PARTY_REPLY_ACCEPT_MAKE;
(window as any).PARTY_REPLY_NOT_FOUND = PARTY_REPLY_NOT_FOUND;
(window as any).PARTY_REPLY_BUSY = PARTY_REPLY_BUSY;
(window as any).PARTY_REPLY_REJECT_JOIN = PARTY_REPLY_REJECT_JOIN;
(window as any).PARTY_REPLY_DESTROY = PARTY_REPLY_DESTROY;
(window as any).PARTY_REPLY_FULL_MEMBERS = PARTY_REPLY_FULL_MEMBERS;
(window as any).PARTY_REPLY_INVALID_LEVEL = PARTY_REPLY_INVALID_LEVEL;
(window as any).PARTY_REPLY_CHANGE_OWNER = PARTY_REPLY_CHANGE_OWNER;
(window as any).PARTY_REPLY_CHANGE_OWNERnDISCONN = PARTY_REPLY_CHANGE_OWNERnDISCONN;
(window as any).PARTY_REPLY_NO_CHARGE_TARGET = PARTY_REPLY_NO_CHARGE_TARGET;
(window as any).PARTY_REPLY_DISCONNECT = PARTY_REPLY_DISCONNECT;
(window as any).PARTY_REPLY_BAN = PARTY_REPLY_BAN;
(window as any).PARTY_REPLY_SHARE_ENABLED = PARTY_REPLY_SHARE_ENABLED;
(window as any).PARTY_REPLY_SHARE_DISABLED = PARTY_REPLY_SHARE_DISABLED;
(window as any).PARTY_RULE_ITEM_TO_ORDER = PARTY_RULE_ITEM_TO_ORDER;
(window as any).PARTY_RULE_EXP_PER_PLAYER = PARTY_RULE_EXP_PER_PLAYER;
(window as any).PARTY_MEMBER_SUB = PARTY_MEMBER_SUB;
(window as any).PartyData = PartyData;
(window as any).REPLY_GET_FIELDITEM_REPLY_OK = REPLY_GET_FIELDITEM_REPLY_OK;
(window as any).REPLY_GET_FIELDITEM_REPLY_NONE = REPLY_GET_FIELDITEM_REPLY_NONE;
(window as any).REPLY_GET_FIELDITEM_REPLY_NO_RIGHT = REPLY_GET_FIELDITEM_REPLY_NO_RIGHT;
(window as any).REPLY_GET_FIELDITEM_REPLY_TOO_MANY = REPLY_GET_FIELDITEM_REPLY_TOO_MANY;

// =============================================================================
// Console Logging to Server
// =============================================================================
(function setupConsoleLogging() {
    // Store original console methods
    const originalConsoleLog = console.log;
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;
    const originalConsoleInfo = console.info;
    const originalConsoleDebug = console.debug;

    // Function to send logs to server
    function sendLogToServer(type: string, args: any[]) {
        const timestamp = new Date().toISOString();
        const message = Array.prototype.slice
            .call(args)
            .map(function (arg) {
                if (typeof arg !== "object" || arg === null) {
                    return String(arg);
                }
                try {
                    // Try to stringify with circular reference handler
                    const seen = new WeakSet();
                    return JSON.stringify(
                        arg,
                        function (key, value) {
                            if (typeof value === "object" && value !== null) {
                                if (seen.has(value)) {
                                    return "[Circular]";
                                }
                                seen.add(value);
                            }
                            return value;
                        },
                        2
                    );
                } catch (e) {
                    // If stringify fails, return string representation
                    return String(arg);
                }
            })
            .join(" ");

        // Send log to server asynchronously (don't block on errors)
        fetch("/savelogs", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                type: type,
                message: message,
                timestamp: timestamp,
            }),
        }).catch(function (err) {
            // Silently fail if we can't send logs to server
            // Use original console to avoid infinite loop
            originalConsoleError.call(
                console,
                "Failed to send log to server:",
                err
            );
        });
    }

    // Override console methods
    console.log = function () {
        sendLogToServer("log", Array.prototype.slice.call(arguments));
        originalConsoleLog.apply(console, arguments);
    };

    console.error = function () {
        sendLogToServer("error", Array.prototype.slice.call(arguments));
        originalConsoleError.apply(console, arguments);
    };

    console.warn = function () {
        sendLogToServer("warn", Array.prototype.slice.call(arguments));
        originalConsoleWarn.apply(console, arguments);
    };

    console.info = function () {
        sendLogToServer("info", Array.prototype.slice.call(arguments));
        originalConsoleInfo.apply(console, arguments);
    };

    console.debug = function () {
        // sendLogToServer('debug', Array.prototype.slice.call(arguments));
        originalConsoleDebug.apply(console, arguments);
    };

    console.log(
        "Console logging initialized - all output will be saved to client.log on server"
    );
})();
// =============================================================================

// Set some default values
// Note: THREE.XHRLoader and THREE.ImageUtils were removed in r150
// CrossOrigin is now set per-loader instance

// Set up the renderer
// Note: THREE.js r137+ defaults to alpha:true, but we want opaque canvas
(window as any).renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: false,
});
// Set outputColorSpace to LinearSRGBColorSpace to match original LinearEncoding behavior
renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
renderer.setPixelRatio(platform.pixelRatio());
renderer.setSize(platform.viewportSize().width, platform.viewportSize().height);
document.body.appendChild(renderer.domElement);
renderer.setClearColor(0x888888, 1);
renderer.autoClear = false;

// Import InputManager after renderer is created (it needs renderer in its constructor)
import("./controls/inputmanager").then((module) => {
    (window as any).InputManager = module.InputManager;
});

// Load any needed extensions
const gl = renderer.getContext();
if (gl instanceof WebGL2RenderingContext) {
    // WebGL 2 core supports min/max blend equations without extensions
} else {
    const minMaxGlExt = renderer.getContext().getExtension("EXT_blend_minmax");
    if (!minMaxGlExt) {
        console.warn("Could not load blend_minmax extension!");
    }
}

// Create a global scene to work with
(window as any).scene = new THREE.Scene();
(window as any).skyScene = new THREE.Scene();
(window as any).skyObject = null;

// Set up some basic initial lighting
const diffuseLevel = new THREE.Color(1.0, 1.0, 1.0);
const ambientLevel = new THREE.Color(0.86, 0.83, 0.8);

diffuseLevel.multiplyScalar(0.6);
ambientLevel.multiplyScalar(0.6);

const directionalLight = new THREE.DirectionalLight(
    diffuseLevel.getHex(),
    Math.PI
);
directionalLight.position.set(0.5, 0.5, 0.5);
scene.add(directionalLight);
global.directionalLight = directionalLight;
// Increase intensity to compensate for three.js r160 lighting changes
global.ambientLight = new THREE.AmbientLight(ambientLevel.getHex(), Math.PI);
scene.add(global.ambientLight);

// Set a global camera to work with
(window as any).camera = new THREE.PerspectiveCamera(
    45,
    platform.viewportSize().width / platform.viewportSize().height,
    0.1,
    1000
);
camera.up.copy(new THREE.Vector3(0, 0, 1));
camera.position.copy(new THREE.Vector3(-15, 15, 15));
camera.lookAt(new THREE.Vector3(0, 0, 0));

camera.position.add(new THREE.Vector3(5200, 5200, 0));

// Auto resize canvas
window.addEventListener("resize", onWindowResize, false);

function onWindowResize() {
    const { width: wW, height: wH } = platform.viewportSize();
    camera.aspect = wW / wH;
    camera.updateProjectionMatrix();
    spriteCam.left = wW / -2;
    spriteCam.right = wW / 2;
    spriteCam.top = wH / 2;
    spriteCam.bottom = wH / -2;
    spriteCam.updateProjectionMatrix();
    renderer.setSize(wW, wH);
}

// Default material for testing with
const defaultMat = new THREE.MeshPhongMaterial({
    color: 0xdddddd,
    specular: 0x009900,
    shininess: 30,
    flatShading: true,
});

// Set up the debugging stuff
(window as any).debugGui = null;
(window as any).debugCamera = null;
// Simple event emitter for debug input (EventEmitter loads later in bundle.js)
(window as any).debugInput = {
    _handlers: {} as Record<string, Function[]>,
    on(event: string, handler: Function) {
        if (!this._handlers[event]) this._handlers[event] = [];
        this._handlers[event].push(handler);
    },
    emit(event: string, ...args: any[]) {
        if (this._handlers[event]) {
            this._handlers[event].forEach((h: Function) => h(...args));
        }
    },
};
(window as any).debugControls = null;
(window as any).debugCamFrust = new THREE.CameraHelper(camera);
// debugBoundingBoxes is now exported from constants.ts

// Defer InputManager setup until bundle.js loads
window.addEventListener("load", function () {
    const debugTriggerKeyCodes = [192, 223]; // ` tilde key
    const inputMgrEventHandler = InputManager._handleEvent;
    InputManager._handleEvent = function (name: any, e: any) {
        if (
            name === "keydown" &&
            debugTriggerKeyCodes.indexOf(e.keyCode) !== -1
        ) {
            DebugHelper.toggleDebugCamera();
            e.preventDefault();
            return;
        }

        if (name === "keydown" && [107, 187].indexOf(e.keyCode) !== -1) {
            // +
            if (!debugCamera) return;

            debugControls.movementSpeed += 10;

            e.preventDefault();
        }

        if (name === "keydown" && [109, 189].indexOf(e.keyCode) !== -1) {
            // -
            if (!debugCamera) return;

            debugControls.movementSpeed -= 10;

            e.preventDefault();
        }

        if (debugControls) {
            debugInput.emit(name, e);
        } else {
            // Use the default handler
            inputMgrEventHandler.call(this, name, e);
        }
    };
});

// FPS / MS indicators (initialized after Stats loads)
let stats: any;
let fpsStats: any;

window.addEventListener("load", function () {
    // FPS / MS indicator
    stats = new Stats();
    stats.setMode(1); // 0: FPS, 1: MS
    stats.domElement.style.position = "absolute";
    stats.domElement.style.left = "0px";
    stats.domElement.style.top = "0px";
    document.body.appendChild(stats.domElement);

    // FPS indicator next to MS
    fpsStats = new Stats();
    fpsStats.setMode(0); // 0: FPS
    fpsStats.domElement.style.position = "absolute";
    fpsStats.domElement.style.left = "80px"; // Position to the right of the MS graph
    fpsStats.domElement.style.top = "0px";
    document.body.appendChild(fpsStats.domElement);

    // Start render loop after everything is loaded
    renderFrame();
});

const wW = renderer.domElement.width;
const wH = renderer.domElement.height;
(window as any).spriteCam = new THREE.OrthographicCamera(
    wW / -2,
    wW / 2,
    wH / 2,
    wH / -2,
    0,
    1
);
const spriteScene = new THREE.Scene();
function _renderSprites() {
    spriteScene.children = [];

    scene.traverseVisible(function (object: any) {
        if (object instanceof OrthoSprite) {
            const sprite: any = object;
            const worldPos: any = sprite.localToWorld(
                new THREE.Vector3(0, 0, 0)
            );
            worldPos.project(camera);
            worldPos.unproject(spriteCam);

            worldPos.x = Math.round(worldPos.x) + sprite.offset.x;
            worldPos.y = Math.round(worldPos.y) - sprite.offset.y;
            sprite.renderSprite.position.copy(worldPos);
            sprite.renderSprite.scale.copy(sprite.scale);

            spriteScene.add(sprite.renderSprite);
        }
    });

    renderer.render(spriteScene, spriteCam);
}

const clock = new THREE.Clock();
const renderFrame = function () {
    requestAnimationFrame(renderFrame);
    stats.begin();
    fpsStats.begin();

    const delta = clock.getDelta();

    // Game middleware: animation handler, underwater detection, weather, zone effects,
    // StateManager, etc.
    game.update(delta);

    let renderCamera = camera;
    if (debugCamera && debugControls) {
        debugCamFrust.update(delta);
        debugControls.update(delta);
        renderCamera = debugCamera;
    }

    const underwater = game.get(WaterMiddleware)?.isUnderwater ?? false;

    // Background colour: deep blue-green when underwater so distant objects
    // (and any unoccluded background) look like open water rather than grey sky.
    renderer.setClearColor(underwater ? 0x021520 : 0x888888, 1);
    renderer.clear(true, true, false);

    if (!underwater && skyObject) {
        // Above water: render sky dome as normal
        camera.updateMatrixWorld();
        skyObject.position.copy(
            camera.localToWorld(new THREE.Vector3(0, 0, 0))
        );
        renderer.render(skyScene, renderCamera);
    } else if (!underwater) {
        renderer.clear(true, true, false);
    }
    // Underwater: skip sky entirely — background is already the water blue from clear

    renderer.render(scene, renderCamera);

    UnderwaterOverlay.render(renderer);

    _renderSprites();

    stats.end();
    fpsStats.end();
};

// Defer all code that depends on bundle.js globals
window.addEventListener("load", async function () {
    // A client parameter prefixed with : indicates its the launch state.
    for (let i in config) {
        if (i[0] === ":") {
            if (config.state) {
                console.warn("More than one launch state specified!");
            }
            config.state = i.substr(1);
            delete config[i];
        }
    }

    // Pick the launch state
    console.log("config", config);
    const launchStateName = config.state ? config.state : "login";
    console.info("Launching game with state `" + launchStateName + "`");

    // Default the load screen off if state was manually set
    if (config.state) {
        LoadScreen.hide();
    }

    // ShaderMiddleware registers and loads all shaders; StateMiddleware drives
    // StateManager.update() each frame. Both are wired via RoseOnlineMiddleware.
    game.use(new RoseOnlineMiddleware());
    await game.init({
        scene: scene as THREE.Scene,
        getActiveCamera: () => (window as any).camera as THREE.Camera,
        renderer: renderer as THREE.WebGLRenderer,
        ambientLight: (global as any).ambientLight as THREE.AmbientLight,
        directionalLight,
    });

    // Shaders loaded, GUI ready — launch the initial game state.
    StateManager.prepareAndSwitch(launchStateName);
    // Data table registrations → DataMiddleware (runs during game.init above)

}); // end window load event
